import { useEffect, useMemo, useState } from 'react';
import { useDebounce } from 'use-debounce';
import {
  CircularProgress, Table, TableContainer, Dialog, DialogActions, TableBody,
  TableRow, TableHead, TableCell, IconButton, DialogTitle, DialogContentText, DialogContent, Grid, Typography, Tooltip, Checkbox,
  Stack,
  Switch,
  Box,
} from '@mui/material';
import { Button, Form, useTranslate } from 'react-admin';
import { Close, ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import type { FieldValues } from 'react-hook-form';
import { SearchInput } from './SearchInput';
import { LIST_DEBOUNCE } from '../../../config';
import type { CertificationCategoryPreset, CertificationCategoryPresetKey } from '../entities/certifications';
import type { InventoryCategoryPreset, InventoryCategoryPresetKey } from '../entities/inventoryCategories';
import type { NotifiedPeopleFormValue } from './inputs/NotifiedPeopleInput';
import { NotifiedPeopleInput } from './inputs/NotifiedPeopleInput';

export type CategoryPreset = CertificationCategoryPreset | InventoryCategoryPreset;
export type CategoryPresetKey = CertificationCategoryPresetKey | InventoryCategoryPresetKey;

type MappedPreset = CategoryPreset & {
  exists: boolean;
};

type DialogStep = 'presets' | 'peopleToNotify';

type CreateFromPresetDialogProps = {
  open: boolean;
  onClose: () => void;
  onSubmit: (
    selectedPresetKeys: CategoryPresetKey[],
    notifiedPeople: NotifiedPeopleFormValue,
    documentaryProofByPresetKey?: Partial<Record<CategoryPresetKey, boolean>>,
  ) => void | Promise<void>;
  onAddCustom?: () => void;
  isSubmitting?: boolean;
  presetPrefix: string;
  showCertifiedUserOption?: boolean;
  showCertifiedUsersStep?: boolean;
  presets: CategoryPreset[];
  existingNamesSet: Set<string>;

  i18n: {
    title: string;
    subtitle?: string;
    searchPlaceholder: string;
    noResults: string;
    presetAlreadyExists: string;
    peopleToNotifyTitle: string;
    peopleToNotifyLabel: string;
    peopleToNotifyHelper: string;
  };

  renderExtraHeadCells: () => React.ReactNode;
  renderExtraBodyCells: (row: MappedPreset, textColor: string) => React.ReactNode;
  showDocumentaryProofToggles?: boolean;
};

export const CreateFromPresetDialog = (props: CreateFromPresetDialogProps) => {

  const {
    open, onClose, onSubmit, i18n, presetPrefix, presets, existingNamesSet,
    renderExtraHeadCells, renderExtraBodyCells, isSubmitting = false,
    showCertifiedUserOption = false,
    showCertifiedUsersStep = false,
    showDocumentaryProofToggles = false,
    onAddCustom,
  } = props;

  const translate = useTranslate();
  const [selectedPresetKeys, setSelectedPresetKeys] = useState<CategoryPresetKey[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm] = useDebounce(searchTerm, LIST_DEBOUNCE);
  const [step, setStep] = useState<DialogStep>('presets');

  const [documentaryProofByPresetKey, setDocumentaryProofByPresetKey] = useState<
  Partial<Record<CategoryPresetKey, boolean>>
>({});

  const isPdfRequired = (key: CategoryPresetKey) => documentaryProofByPresetKey[key] ?? true;

  const peopleFormDefaultValues = {
    notifiedPeople: {
      peopleToNotify: [],
      emails: [],
      ...(showCertifiedUserOption ? { isCertificationHolderNotified: true } : {}),
    },
  };

  const handleClose = () => {

    setStep('presets');
    setSelectedPresetKeys([]);
    setDocumentaryProofByPresetKey({});
    setSearchTerm('');
    onClose();

  };

  const handlePeopleFormSubmit = async (data: FieldValues) => {

    const notifiedPeople: NotifiedPeopleFormValue = {
      peopleToNotify: data.notifiedPeople?.peopleToNotify ?? [],
      emails: data.notifiedPeople?.emails ?? [],
      ...(showCertifiedUserOption
        ? { isCertificationHolderNotified: data.notifiedPeople?.isCertificationHolderNotified }
        : {}),
    };

    await onSubmit(selectedPresetKeys, notifiedPeople, showDocumentaryProofToggles ? documentaryProofByPresetKey : undefined);

  };

  const handleToggleSelect = (presetKey: CategoryPresetKey, isDisabled: boolean) => {

    if (isDisabled) return;

    const isSelected = selectedPresetKeys.includes(presetKey);

    if (isSelected) {

      setSelectedPresetKeys((current) => current.filter((p) => p !== presetKey));
      setDocumentaryProofByPresetKey((prev) => {

        const next = { ...prev };
        delete next[presetKey];
        return next;

      });
      return;

    }
    setSelectedPresetKeys((current) => [...current, presetKey]);

  };

  const filteredPresets = useMemo(() => {

    const q = debouncedSearchTerm.trim().toLowerCase();
    if (!q) return presets;

    return presets.filter((preset) => {

      const name = translate(`${presetPrefix}.${preset.presetKey}.name`).toLowerCase();
      const description = translate(`${presetPrefix}.${preset.presetKey}.description`).toLowerCase();
      return name.includes(q) || description.includes(q);

    });

  }, [debouncedSearchTerm, presets, presetPrefix, translate]);

  const mappedPresets = useMemo(() => {

    return filteredPresets.map((preset) => ({

      ...preset,
      exists: existingNamesSet.has(translate(`${presetPrefix}.${preset.presetKey}.name`)),

    }))
      .sort((a, b) => {

        if (a.exists && !b.exists) return 1;
        if (!a.exists && b.exists) return -1;
        return 0;

      });

  }, [filteredPresets, existingNamesSet, presetPrefix, translate]);

  const showEmptyState = mappedPresets.length === 0;

  useEffect(() => {

    if (!open) {

      setStep('presets');
      setSelectedPresetKeys([]);
      setDocumentaryProofByPresetKey({});
      setSearchTerm('');

    }

  }, [open]);

  const handleNext = () => {

    if (selectedPresetKeys.length === 0) return;
    setStep('peopleToNotify');

  };

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth="lg"
    >
      <DialogTitle className="flex-in-between">
        <Stack direction="row" spacing={1} alignItems="center">
          {step === 'peopleToNotify' && (
            <IconButton onClick={() => setStep('presets')} aria-label={translate('ra.action.back')} disabled={isSubmitting}>
              <ArrowBackIcon />
            </IconButton>
          )}
          <Typography variant="body1" fontSize="20px">
            {translate(step === 'presets' ? i18n.title : i18n.peopleToNotifyTitle)}
          </Typography>
        </Stack>
        <IconButton onClick={handleClose}><Close /></IconButton>
      </DialogTitle>

      {step === 'presets' && (
        <>
          <DialogContent>
            <Grid display="flex" flexDirection="row" gap={2} justifyContent="space-between" alignItems="center" marginTop={2}>
              <Grid item xs>
                {i18n.subtitle && <DialogContentText>{translate(i18n.subtitle)}</DialogContentText>}
              </Grid>
              <Stack direction="row" spacing={2} alignItems="center" sx={{ flexShrink: 0 }}>
                {onAddCustom && (
                  <Button
                    label="general.text.add_custom_category"
                    size="medium"
                    variant="contained"
                    onClick={onAddCustom}
                    disabled={isSubmitting}
                    sx={{ whiteSpace: 'nowrap' }}
                  />
                )}
                <Box sx={{ minWidth: 240 }}>
                  <SearchInput
                    searchTerm={searchTerm}
                    onChange={setSearchTerm}
                    placeholder={translate(i18n.searchPlaceholder)}
                    variant="standard"
                  />
                </Box>
              </Stack>
            </Grid>
            {showEmptyState && (
              <Typography variant="body1" sx={{ textAlign: 'center', marginTop: '20px' }}>
                {translate(i18n.noResults)}
              </Typography>
            )}
            {!showEmptyState && (
              <TableContainer className="bhvk-table">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox" />
                      <TableCell>{translate('general.fields.name')}</TableCell>
                      {renderExtraHeadCells()}
                      {showDocumentaryProofToggles && (
                        <TableCell>{translate('resources.assets.certifications.fields.documentary_proof')}</TableCell>
                      )}
                      <TableCell>{translate('general.fields.description')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {mappedPresets.map((row) => {

                      const isSelected = selectedPresetKeys.includes(row.presetKey);
                      const isDisabled = row.exists;
                      const textColor = isDisabled ? 'text.disabled' : 'text.primary';
                      return (
                        <Tooltip
                          key={row.presetKey}
                          title={isDisabled ? translate(i18n.presetAlreadyExists) : ''}
                        >
                          <TableRow
                            onClick={() => handleToggleSelect(row.presetKey, isDisabled)}
                            hover
                            selected={isSelected}
                          >
                            <TableCell padding="checkbox">
                              <Checkbox checked={isSelected} value={row.presetKey} disabled={isDisabled} />
                            </TableCell>
                            <TableCell sx={{ color: textColor }}>
                              {translate(`${presetPrefix}.${row.presetKey}.name`)}
                            </TableCell>
                            {renderExtraBodyCells(row, textColor)}
                            {showDocumentaryProofToggles && (
                              <TableCell padding="none" onClick={(e) => e.stopPropagation()}>
                                <Switch
                                  size="small"
                                  checked={isPdfRequired(row.presetKey)}
                                  disabled={isDisabled || !isSelected}
                                  onChange={(_, checked) => {

                                    setDocumentaryProofByPresetKey((prev) => ({
                                      ...prev,
                                      [row.presetKey]: checked,
                                    }));

                                  }}
                                  inputProps={{
                                    'aria-label': translate('resources.assets.certifications.fields.documentary_proof'),
                                  }}
                                />
                              </TableCell>
                            )}
                            <TableCell sx={{ color: textColor }}>
                              {translate(`${presetPrefix}.${row.presetKey}.description`)}
                            </TableCell>
                          </TableRow>
                        </Tooltip>
                      );

                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              label="general.text.next"
              size="medium"
              onClick={handleNext}
              startIcon={isSubmitting ? <CircularProgress color="inherit" size={20} /> : undefined}
              disabled={selectedPresetKeys.length === 0}
            />
          </DialogActions>
        </>
      )}

      <Form
        id="create-from-preset-people-form"
        defaultValues={peopleFormDefaultValues}
        onSubmit={handlePeopleFormSubmit}
      >
        {step === 'peopleToNotify' && (
          <>
            <DialogContent>
              <NotifiedPeopleInput
                isRequired
                showCertifiedUserOption={showCertifiedUserOption}
                label={i18n.peopleToNotifyLabel}
                helperText={i18n.peopleToNotifyHelper}
                inDialog
              />
            </DialogContent>
            <DialogActions>
              <Button
                type="submit"
                form="create-from-preset-people-form"
                label={
                  showCertifiedUsersStep && selectedPresetKeys.length === 1
                    ? 'general.text.next'
                    : 'general.text.add'
                }
                size="medium"
                startIcon={isSubmitting ? <CircularProgress color="inherit" size={20} /> : undefined}
                disabled={isSubmitting}
              />
            </DialogActions>
          </>
        )}
      </Form>
    </Dialog>
  );

};
